import React from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import { Calendar, User, ArrowRight, Sparkles } from "lucide-react";

export default function Blog() {
  const blogPosts = [
    {
      id: "swift-therapy-guide",
      title: "Why Swift Microwave Therapy is Changing How We Treat Plantar Warts",
      date: "September 15, 2026",
      author: "Dr. Spence Harper, DPM",
      excerpt: "Traditional wart treatments like freezing, acid peeling, or surgical excision treat only the surface tissue. Learn how Swift stimulates your immune defenses to conquer HPV at the cellular root.",
      category: "Wart Care",
      readTime: "4 min read"
    },
    {
      id: "morning-heel-pain-plantar-fasciitis",
      title: "First Steps in the Morning Feel Like Broken Glass? Understanding Plantar Fasciitis",
      date: "August 28, 2026",
      author: "Dr. Spence Harper, DPM",
      excerpt: "Sharp morning heel pain is the hallmark sign of plantar fasciitis. Discover how evaluation, stretching, footwear changes, and custom orthotics can support recovery.",
      category: "Heel Pain",
      readTime: "5 min read"
    },
    {
      id: "modern-bunion-surgery-recovery",
      title: "Modern Bunion Correction: How Advances in Podiatric Surgery Reduce Downtime",
      date: "July 19, 2026",
      author: "Dr. Spence Harper, DPM",
      excerpt: "Older bunion procedures required months of non-weight-bearing recovery. Today's anatomically stable osteotomies allow quicker mobility and beautiful structural alignment.",
      category: "Surgical Advances",
      readTime: "6 min read"
    }
  ];

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Wasatch Foot & Ankle Center - Patient Education Blog",
    url: "https://www.drspenceharper.com/blog/",
    description: "Podiatry news, clinical insights, and patient education on plantar warts, bunions, heel pain, and foot surgery in Heber City, UT.",
    blogPost: blogPosts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.excerpt,
      datePublished: "2026-09-01",
      author: {
        "@type": "Person",
        name: p.author
      }
    }))
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Foot & Ankle Health Blog | Wasatch Foot & Ankle Center"
        description="Read educational foot and ankle health articles from board-certified podiatrist Dr. Spence Harper. Insights on Swift microwave therapy, bunions, and heel pain in Heber City, UT."
        canonicalPath="/blog/"
        schema={blogSchema}
      />
      <Header />
      <Breadcrumbs items={[{ label: "Foot Health Blog" }]} />

      <main className="flex-1">
        {/* HERO TITLE */}
        <section className="bg-[#0f2b48] text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-3">
            <span className="text-cyan-400 font-bold text-xs uppercase tracking-widest">
              Patient Education &bull; Clinical Insights
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              Foot &amp; Ankle Health Articles
            </h1>
            <p className="text-slate-300 text-base max-w-2xl mx-auto font-body">
              Expert guidance from board-certified podiatric surgeon Dr. Spence Harper to help you understand your symptoms, treatment options, and proactive foot health.
            </p>
          </div>
        </section>

        {/* REQUIRED DEARDOC BLOG CONTAINER */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Required DearDoc blog post container: exactly <div id="dib-posts"></div> */}
            <div id="dib-posts" className="min-h-[10px]"></div>

            {/* Curated Fallback Educational Posts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                >
                  <div className="p-7 space-y-4">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="font-bold text-[#0284c7] uppercase tracking-wider">
                        {post.category}
                      </span>
                      <span>{post.readTime}</span>
                    </div>

                    <h2 className="font-heading font-bold text-xl text-[#0f2b48] leading-snug">
                      {post.title}
                    </h2>

                    <p className="text-sm text-slate-600 leading-relaxed font-body">
                      {post.excerpt}
                    </p>
                  </div>

                  <div className="px-7 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {post.date}
                    </span>
                  </div>
                </article>
              ))}
            </div>

            {/* Consultation CTA */}
            <div className="mt-16 bg-slate-50 border border-slate-200 rounded-3xl p-8 sm:p-12 text-center space-y-4">
              <h3 className="font-heading text-2xl font-bold text-[#0f2b48]">
                Have a Question About a Foot or Ankle Condition?
              </h3>
              <p className="text-sm text-slate-600 max-w-xl mx-auto">
                Schedule a consultation at our Heber City clinic with Dr. Harper to receive an accurate diagnosis and individualized treatment plan.
              </p>
              <div className="pt-2">
                <Link
                  href="/appointments/"
                  className="inline-flex items-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold text-sm px-6 py-3 rounded-lg shadow transition-all"
                >
                  <Calendar className="w-4 h-4" /> Request Appointment Online
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
